import React, { MutableRefObject } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { AppRoutes } from 'app/static/routes/routes-enum';
import { ReactComponent as PairsTokens } from 'app/components/svg/PairsTokens.svg';
import TokenImage from 'app/components/ui/TokenImage';
import { getSlice, getTokenLogo, getUniqueKey } from 'app/utils/helpers';
import Button from 'app/components/ui/Button';
import Version from 'app/components/ui/Version';

import s from './Dropdown.module.sass';

export type SelectedPairsDataType = {
  id: number
  firstTokenName: string
  secondTokenName: string
  firstTokenImage: string
  secondTokenImage: string
};

type DropdownProps = {
  data: {
    tokens?: any
    pairs?: any
  }
  className?: string
  onClick?: () => void
  ref?: MutableRefObject<null>
  select?: boolean
  setSelectedPairs?: (arg: SelectedPairsDataType | string) => void
  setActive?: (arg: boolean) => void
  showSeeAll?: boolean
  selectedPairs?: SelectedPairsDataType | string
  loading?: boolean
};

type DropdownSectionProps = {
  data?: any
  title: string
  onClick?: () => void
  select?: boolean
  setSelectedPairs?: (arg: SelectedPairsDataType | string) => void
  setActive?: (arg: boolean) => void
  showSeeAll?: boolean
  selectedPairs?: SelectedPairsDataType | string
  type?: 'tokens' | 'pairs'
  loading?: boolean
};

const DropdownSection: React.FC<DropdownSectionProps> = ({
  title,
  data,
  select = false,
  setSelectedPairs,
  setActive,
  showSeeAll,
  selectedPairs,
  type = 'tokens',
  loading,
}) => {
  const handleSelect = (pairsData: SelectedPairsDataType | string) => {
    setSelectedPairs!(pairsData);

    setActive!(false);
  };

  if (loading) {
    return (
      <div className={cx(s.section)}>
        <h3 className={cx(s.title)}>{title}</h3>

        <div className={cx(s.wrapper)}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (select) {
    return (
      <>
        {showSeeAll && (selectedPairs !== 'All pairs') && (
        <div className={cx(s.allPairs)} onClick={() => handleSelect!('All pairs')}>
          <PairsTokens className={cx(s.pairsSvg)} />
          All pairs
        </div>
        )}

        {
        data.map(({
          id, firstTokenImage, firstTokenName, secondTokenImage, secondTokenName,
        }: any) => {
          const currentItem = {
            id,
            firstTokenName,
            secondTokenName: secondTokenName!,
            firstTokenImage,
            secondTokenImage: secondTokenImage!,
          };

          const selectedItem = selectedPairs?.valueOf() as SelectedPairsDataType;

          if (selectedItem.id !== id) {
            return (
              <div
                key={id}
                className={cx(s.token)}
                onClick={() => handleSelect!(currentItem)}
              >
                <TokenImage
                  firstTokenImage={firstTokenImage}
                  secondTokenImage={secondTokenImage}
                  tokensNames={{ firstTokenName, secondTokenName }}
                  sizeT="medium"
                />
                <span className={cx(s.tokenName)}>
                  {`${firstTokenName} / ${secondTokenName}`}
                </span>
              </div>
            );
          }

          return null;
        })
      }
      </>
    );
  }

  if (type === 'tokens') {
    return (
      <div className={cx(s.section)}>
        <h3 className={cx(s.title)}>{title}</h3>

        <div className={cx(s.wrapper)}>
          {data && data.length ? data.map(({
            id, tokenId, name, symbol, icon,
          }: any) => (
            <Link
              key={getUniqueKey()}
              to={`${AppRoutes.TOKENS}/${id}_${tokenId}`}
              className={cx(s.token)}
            >
              <TokenImage
                firstTokenImage={icon ? getTokenLogo(icon) : null}
                tokensNames={{ firstTokenName: name ?? (symbol ?? 'Unnamed') }}
                sizeT="medium"
              />
              <span className={cx(s.tokenName)}>
                {name ?? (symbol ?? 'Unnamed')}
              </span>
            </Link>
          )) : (
            <div>No results</div>
          )}
        </div>

        <Button
          to={AppRoutes.TOKENS}
          theme="senary"
          sizeT="small"
          className={cx(s.button)}
        >
          See more
        </Button>
      </div>
    );
  }

  return (
    <div className={cx(s.section)}>
      <h3 className={cx(s.title)}>{title}</h3>

      <div className={cx(s.wrapper)}>
        {data && data.length ? data.map(({
          id, token1, token2, version,
        }: any) => (
          <Link
            key={getUniqueKey()}
            to={`${AppRoutes.PAIRS}/${id}`}
            className={cx(s.token)}
          >
            <div className={cx(s.tokensWrapper)}>
              <TokenImage
                firstTokenImage={
                token1.icon ? getTokenLogo(token1.icon) : null
              }
                secondTokenImage={
                token2.icon ? getTokenLogo(token2.icon) : null
              }
                tokensNames={{ firstTokenName: token1.symbol ?? 'Unnamed', secondTokenName: getSlice(token2.symbol) ?? 'Unnamed' }}
                sizeT="medium"
              />
              <span className={cx(s.tokenName)}>
                {`${token1.symbol ?? 'Unnamed'} / ${(getSlice(token2.symbol) ?? 'Unnamed')}`}
              </span>

              <Version version={version} className={cx(s.version)} />
            </div>
          </Link>
        )) : (
          <div>No results</div>
        )}
      </div>

      <Button
        to={AppRoutes.PAIRS}
        theme="senary"
        sizeT="small"
        className={cx(s.button)}
      >
        See more
      </Button>
    </div>
  );
};

const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(({
  className,
  data,
  onClick,
  select,
  setSelectedPairs,
  setActive,
  showSeeAll,
  selectedPairs,
  loading,
}, ref) => {
  const { tokens, pairs } = data;

  const compoundClassName = cx(
    s.root,
    className,
  );

  return (
    <div className={compoundClassName} ref={ref}>
      <DropdownSection
        title="Pairs"
        data={pairs}
        onClick={onClick}
        select={select}
        setSelectedPairs={setSelectedPairs}
        setActive={setActive}
        showSeeAll={showSeeAll}
        selectedPairs={selectedPairs}
        type="pairs"
        loading={loading}
      />
      <DropdownSection title="Tokens" data={tokens} onClick={onClick} loading={loading} />
    </div>
  );
});

export default Dropdown;
